import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/api';
import type { SendGridSettings } from '../../types';

interface SendGridTabProps {
  settings: SendGridSettings;
  onChange: (nextSettings: SendGridSettings) => void;
  onManageEmailTemplates?: () => void;
}

const INPUT_CLASS_NAME = 'mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

function getErrorMessage(error: unknown): string {
  const apiError = error as { response?: { data?: { title?: string; detail?: string; message?: string } }; message?: string };
  return apiError.response?.data?.detail
    ?? apiError.response?.data?.title
    ?? apiError.response?.data?.message
    ?? apiError.message
    ?? 'Failed to send test email';
}

export function SendGridTab({ settings, onChange, onManageEmailTemplates }: SendGridTabProps) {
  const { user } = useAuth();
  const [showApiKey, setShowApiKey] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState(user?.email ?? '');
  const [testEmailResult, setTestEmailResult] = useState<{ success: boolean; message: string } | null>(null);

  const sendTestEmailMutation = useMutation({
    mutationFn: (toEmail: string) => apiClient.sendTestEmail({ toEmail }),
    onSuccess: (result) => {
      setTestEmailResult(result);
    },
    onError: (error) => {
      setTestEmailResult({ success: false, message: getErrorMessage(error) });
    },
  });

  const isEnabled = Boolean(settings.enabled);

  const handleSendTestEmail = () => {
    if (!testEmailAddress.trim()) {
      setTestEmailResult({ success: false, message: 'Please enter an email address' });
      return;
    }

    setTestEmailResult(null);
    sendTestEmailMutation.mutate(testEmailAddress.trim());
  };

  const updateSetting = <K extends keyof SendGridSettings>(key: K, value: SendGridSettings[K]) => {
    onChange({ ...settings, [key]: value });
  };

  const handleToggleApiKeyVisibility = () => {
    setShowApiKey((previous) => !previous);
  };

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">SendGrid Email Configuration</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Configure SendGrid for sending email notifications</p>
        </div>
        <label className="relative inline-flex cursor-pointer items-center">
          <input
            type="checkbox"
            className="peer sr-only"
            checked={isEnabled}
            onChange={(event) => updateSetting('enabled', event.target.checked)}
            aria-label="Enable or disable SendGrid"
          />
          <div className="h-6 w-11 rounded-full bg-gray-200 transition peer-checked:bg-primary-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full peer-checked:after:border-white dark:bg-gray-600"></div>
          <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-100">{isEnabled ? 'Enabled' : 'Disabled'}</span>
        </label>
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="sendgrid-api-key" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            SendGrid API Key <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              id="sendgrid-api-key"
              type={showApiKey ? 'text' : 'password'}
              className={`${INPUT_CLASS_NAME} pr-20`}
              value={settings.apiKey}
              onChange={(event) => onChange({
                ...settings,
                apiKey: event.target.value,
                hasApiKey: settings.hasApiKey || event.target.value.trim().length > 0,
              })}
              placeholder={settings.hasApiKey ? '••••••••••••••••••••••••••' : 'SG.xxxxxxxxxxxxxxxxxxxxxxxxxxx'}
              disabled={!isEnabled}
              aria-required="true"
              aria-disabled={!isEnabled}
            />
            <button
              type="button"
              onClick={handleToggleApiKeyVisibility}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
              aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
              aria-pressed={showApiKey}
            >
              {showApiKey ? 'Hide' : 'Show'}
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {settings.hasApiKey
              ? 'A key is already configured and remains unchanged unless you enter a new value. '
              : 'No API key configured yet. '}
            Get your API key from{' '}
            <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="text-primary-700 hover:underline">
              SendGrid Dashboard
            </a>
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="from-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              From Email <span className="text-red-500">*</span>
            </label>
            <input
              id="from-email"
              type="email"
              className={INPUT_CLASS_NAME}
              value={settings.fromEmail}
              onChange={(event) => updateSetting('fromEmail', event.target.value)}
              placeholder="noreply@yourdomain.com"
              disabled={!isEnabled}
              aria-required="true"
              aria-disabled={!isEnabled}
            />
          </div>

          <div>
            <label htmlFor="from-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              From Name <span className="text-red-500">*</span>
            </label>
            <input
              id="from-name"
              type="text"
              className={INPUT_CLASS_NAME}
              value={settings.fromName}
              onChange={(event) => updateSetting('fromName', event.target.value)}
              placeholder="CloudIncidents"
              disabled={!isEnabled}
              aria-required="true"
              aria-disabled={!isEnabled}
            />
          </div>
        </div>

        {isEnabled && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
            <h4 className="mb-2 text-sm font-semibold text-green-900 dark:text-green-200">Test Email Configuration</h4>
            <p className="mb-3 text-sm text-green-700 dark:text-green-400">
              Send a test email to verify that your SendGrid configuration is working correctly.
            </p>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label htmlFor="test-email-address" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Recipient Email Address
                </label>
                <input
                  id="test-email-address"
                  type="email"
                  className={INPUT_CLASS_NAME}
                  value={testEmailAddress}
                  onChange={(event) => setTestEmailAddress(event.target.value)}
                  placeholder="admin@example.com"
                  aria-label="Test email recipient address"
                />
              </div>
              <button
                type="button"
                onClick={handleSendTestEmail}
                disabled={sendTestEmailMutation.isPending || !testEmailAddress.trim()}
                className="rounded-md bg-primary-700 px-4 py-2 text-sm font-medium text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
                aria-busy={sendTestEmailMutation.isPending}
              >
                {sendTestEmailMutation.isPending ? 'Sending...' : 'Send Test Email'}
              </button>
            </div>

            {testEmailResult && (
              <div
                className={`mt-3 rounded-lg border p-3 ${testEmailResult.success
                  ? 'border-green-300 bg-green-100 dark:border-green-700 dark:bg-green-900/30'
                  : 'border-red-300 bg-red-100 dark:border-red-700 dark:bg-red-900/30'}`}
                role="alert"
              >
                <p className={`text-sm ${testEmailResult.success ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}>
                  {testEmailResult.success ? '✓ ' : '✗ '}
                  {testEmailResult.message}
                </p>
              </div>
            )}
          </div>
        )}

        {isEnabled && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
            <h4 className="mb-2 text-sm font-semibold text-blue-900 dark:text-blue-200">Email Templates</h4>
            <p className="mb-3 text-sm text-blue-700 dark:text-blue-400">
              Configure email templates for different notification types:
            </p>
            <ul className="list-inside list-disc space-y-1 text-sm text-blue-700 dark:text-blue-400">
              <li>Incident Created</li>
              <li>Incident Assigned</li>
              <li>Incident Escalated</li>
              <li>SLA Breach Warning</li>
              <li>SLA Breach</li>
            </ul>
            <button
              type="button"
              onClick={onManageEmailTemplates}
              className="mt-3 text-sm font-medium text-primary-700 hover:text-primary-800 dark:text-primary-300"
            >
              Manage Email Templates →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
